import type { OrderResponse, SubscriptionTypeDto } from 'api/generated/types.gen';
import { getMergedSubscriptionGroups } from 'lib/helpers/order-helpers';
import { ticketKey } from 'lib/helpers/pay-selection-helpers';

/** The rules a subscription was sold under, as the cart needs them. */
export type SubscriptionLimits = Pick<SubscriptionTypeDto, 'minTickets' | 'minEvents' | 'allEventsRequired' | 'pickPerEvent'>;

/** Which rule the removal would break, and the number it would fall short of. */
export type SubscriptionLimitViolation = {
  kind: 'seats' | 'events';
  min: number;
};

/**
 * Whether what would be left of the order's subscriptions still satisfies the
 * rules they were sold under. A subscription is sold as a number of seats
 * across a number of events, and dropping below either leaves one the venue
 * cannot honour, so the removal is refused before it is ever sent.
 *
 * Subscriptions the limits are not (yet) known for are passed over: the split
 * enforces the same rules server-side, so an unanswered lookup costs the buyer
 * a later refusal rather than a wrong one now.
 */
export const findSubscriptionLimitViolation = (order: OrderResponse | undefined, removedKeys: Set<string>, limitsByTypeId: Map<number, SubscriptionLimits>): SubscriptionLimitViolation | undefined => {
  for (const group of getMergedSubscriptionGroups(order)) {
    const limits = limitsByTypeId.get(group.subscriptionTypeId ?? -1);
    if (!limits) continue;

    // One seat per event per seat-set, so what an event holds is the number of
    // seats the subscription still has.
    const seatsPerEvent = (group.eventTicketGroups ?? []).map(eventGroup => (eventGroup.tickets ?? []).filter(ticket => !removedKeys.has(ticketKey(ticket))).length);
    const keptEvents = seatsPerEvent.filter(seats => seats > 0);
    // Nothing of the subscription is left, which is letting go of the whole of
    // it rather than leaving one short.
    if (keptEvents.length === 0) continue;

    // A subscription that has to cover everything it was sold with can lose no
    // event at all, whatever its own minimum says.
    const minEvents = limits.allEventsRequired ? seatsPerEvent.length : limits.minEvents ?? 0;
    if (keptEvents.length < minEvents) {
      return {
        kind: 'events',
        min: minEvents
      };
    }

    // A "pick per event" subscription is sold without a fixed number of seats
    // per event — the buyer chooses how many each one gets — so its seat
    // minimum says nothing about what any single event has to keep.
    if (limits.pickPerEvent) continue;
    const minSeats = limits.minTickets ?? 0;
    if (Math.min(...keptEvents) < minSeats) {
      return {
        kind: 'seats',
        min: minSeats
      };
    }
  }
  return undefined;
};